import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import { Menu, Popover } from "@/components/Base/Headless";
import Pagination from "@/components/Base/Pagination";
import { FormInput, FormSelect } from "@/components/Base/Form";
import Button from "@/components/Base/Button";
import Table from "@/components/Base/Table";
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';

type Outlet = {
    id: string;
    name: string;
    code?: string;
    address?: string;
    city?: string;
    stateId?: string;
    outletGroup?: string;
    outletType?: string;
    outletCategory?: string;
    contact?: string;
    contactPIC?: string;
    imageUrl?: string;
    status: boolean;
    createdAt: string;
    updatedAt: string;
    postcode: string;
    country: string;
    gpsLat: string;
    gpsLong: string;
    companyId: string;
    ownerId: string;
    projectId: string;
};

function OutletsPage() {
    const [outlets, setOutlets] = useState<Outlet[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortField, setSortField] = useState<'name' | 'outletGroup' | 'outletCategory' | 'outletType' | 'createdAt' | 'updatedAt'>('createdAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const companyId = new URLSearchParams(window.location.search).get("companyId");
                const projectId = new URLSearchParams(window.location.search).get("projectId");

                const queryParams = new URLSearchParams();
                if (companyId) queryParams.append("companyId", companyId);
                if (projectId) queryParams.append("projectId", projectId);
                if (searchQuery) queryParams.append("q", searchQuery);
                queryParams.append("page", currentPage.toString());
                queryParams.append("pageSize", pageSize.toString());
                queryParams.append("sortBy", sortField);
                queryParams.append("sortOrder", sortOrder);

                const res = await fetch(
                    `${import.meta.env.VITE_API_URL}/outlets?${queryParams.toString()}`,
                    {
                        headers: { Authorization: `Bearer ${getToken()}` }
                    }
                );
                const data = await res.json();
                setOutlets(Array.isArray(data?.data) ? data.data : []);
                setTotalPages(data.totalPages || 1);
                setTotal(data.total || 0);

            } catch (err) {
                console.error("Failed to fetch outlets:", err);
            } finally {
                setLoading(false);
            }
        })();
    }, [currentPage, pageSize, searchQuery, sortField, sortOrder]);

    const handleDelete = async (id: string) => {
        if (!confirm("Are you sure you want to delete this outlet?")) return;

        try {
            const res = await fetch(`${import.meta.env.VITE_API_URL}/outlets/${id}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${getToken()}`
                }
            });

            if (res.ok) {
                // Refresh the list
                setCurrentPage(1);
            } else {
                const error = await res.json();
                alert(`Failed to delete outlet: ${error?.error || "Unknown error"}`);
            }
        } catch (error: unknown) {
            console.error("Delete failed:", error);
            alert("Something went wrong deleting outlet");
        }
    };

    const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setCurrentPage(1);
    };

    const handleSort = (field: 'name' | 'outletGroup' | 'outletCategory' | 'outletType' | 'createdAt' | 'updatedAt') => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
        setCurrentPage(1);
    };

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handleFirstPage = () => {
        setCurrentPage(1);
    };

    const handleLastPage = () => {
        setCurrentPage(totalPages);
    };

    const handleExport = async () => {
        // Define the template columns in the correct order (matching import requirements)
        const templateColumns = [
            'Name',           // Required
            'Code',
            'Group',          // Outlet Group (must match existing groups)
            'Type',           // Outlet Type (must match existing types)
            'Category',       // Outlet Category (must match existing categories)
            'Address',
            'City',
            'Postcode',
            'State',          // State Name (e.g., "Melaka", "Selangor")
            'Country',
            'Contact',
            'Outlet PIC',
            'Image Url',
            'GPS Lat',
            'GPS Long',
            'Company ID',
            'User ID',
            'Project ID',
            'Status'          // 'Active' or 'Inactive'
        ];

        // Define compulsory columns (to highlight with orange)
        const compulsoryColumns = ['Name', 'Group', 'Type', 'Category', 'State', 'Country'];

        // Fetch ALL outlets (without pagination or filters)
        const companyId = new URLSearchParams(window.location.search).get("companyId");
        const projectId = new URLSearchParams(window.location.search).get("projectId");
        
        let allOutlets: Outlet[] = [];
        try {
            // Fetch all outlets by paginating through all pages
            let currentPage = 1;
            let hasMorePages = true;
            const pageSize = 1000; // Fetch 1000 at a time

            while (hasMorePages) {
                const queryParams = new URLSearchParams();
                if (companyId) queryParams.append("companyId", companyId);
                if (projectId) queryParams.append("projectId", projectId);
                queryParams.append("pageSize", pageSize.toString());
                queryParams.append("page", currentPage.toString());

                const outletsRes = await fetch(
                    `${import.meta.env.VITE_API_URL}/outlets?${queryParams.toString()}`,
                    {
                        headers: { Authorization: `Bearer ${getToken()}` }
                    }
                );
                const outletsData = await outletsRes.json();
                const pageOutlets = Array.isArray(outletsData?.data) ? outletsData.data : [];
                
                if (pageOutlets.length > 0) {
                    allOutlets = [...allOutlets, ...pageOutlets];
                    // Check if there are more pages
                    const totalPages = outletsData.totalPages || 1;
                    hasMorePages = currentPage < totalPages;
                    currentPage++;
                } else {
                    hasMorePages = false;
                }
            }
        } catch (error) {
            console.error('Failed to fetch all outlets for export:', error);
            alert('Failed to fetch outlets for export. Please try again.');
            return;
        }

        // Fetch states to map stateId to state name
        let stateIdToNameMap = new Map<string, string>();
        let states: any[] = [];
        try {
            const statesRes = await fetch(
                `${import.meta.env.VITE_API_URL}/states`,
                {
                    headers: { Authorization: `Bearer ${getToken()}` }
                }
            );
            const statesData = await statesRes.json();
            states = Array.isArray(statesData?.data) ? statesData.data : [];
            states.forEach((state: any) => {
                if (state?.id && state?.name) {
                    stateIdToNameMap.set(state.id, state.name);
                }
            });
        } catch (error) {
            console.error('Failed to fetch states:', error);
        }

        // Prepare outlets data for export
        let exportData: any[] = [];
        
        if (allOutlets.length > 0) {
            // Export actual data from ALL outlets
            exportData = allOutlets.map(outlet => ({
                'Name': outlet.name,
                'Code': outlet.code || '',
                'Group': outlet.outletGroup || '',
                'Type': outlet.outletType || '',
                'Category': outlet.outletCategory || '',
                'Address': outlet.address || '',
                'City': outlet.city || '',
                'Postcode': outlet.postcode || '',
                'State': outlet.stateId ? (stateIdToNameMap.get(outlet.stateId) || outlet.stateId) : '',
                'Country': outlet.country || '',
                'Contact': outlet.contact || '',
                'Outlet PIC': outlet.contactPIC || '',
                'Image Url': outlet.imageUrl || '',
                'GPS Lat': outlet.gpsLat || '',
                'GPS Long': outlet.gpsLong || '',
                'Company ID': outlet.companyId || '',
                'User ID': outlet.ownerId || '',
                'Project ID': outlet.projectId || '',
                'Status': outlet.status ? 'Active' : 'Inactive'
            }));
        } else {
            // Export template with empty row (just headers)
            exportData = [{}];
        }

        // Create workbook using ExcelJS for styling support
        const workbook = new ExcelJS.Workbook();
        const outletsWorksheet = workbook.addWorksheet('Outlets');

        // Add headers
        outletsWorksheet.addRow(templateColumns);

        // Style header row - highlight compulsory columns with orange
        const headerRow = outletsWorksheet.getRow(1);
        headerRow.font = { bold: true };
        headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
        
        templateColumns.forEach((colName, colIndex) => {
            const cell = headerRow.getCell(colIndex + 1);
            if (compulsoryColumns.includes(colName)) {
                // Orange background for compulsory columns
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFFFA500' } // Orange color
                };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }; // White text
            }
        });

        // Add data rows
        exportData.forEach((rowData) => {
            const row = templateColumns.map(col => rowData[col] || '');
            outletsWorksheet.addRow(row);
        });

        // Set column widths
        templateColumns.forEach((colName, colIndex) => {
            outletsWorksheet.getColumn(colIndex + 1).width = Math.max(colName.length + 2, 12);
        });

        // Create States sheet
        const statesWorksheet = workbook.addWorksheet('States');
        statesWorksheet.addRow(['Name']);
        states.forEach((state: any) => {
            statesWorksheet.addRow([state.name || '']);
        });
        statesWorksheet.getColumn(1).width = 20;

        // Generate filename with current date
        const date = new Date().toISOString().split('T')[0];
        const filename = allOutlets.length > 0 
            ? `outlets_export_${date}.xlsx`
            : `outlets_import_template_${date}.xlsx`;

        // Download file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    if (loading && outlets.length === 0) {
        return (
            <ProtectedRoute>
                <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
                            <div>Loading outlets...</div>
                        </div>
                    </div>
                </RoleGate>
            </ProtectedRoute>
        );
    }

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div className="grid grid-cols-12 gap-y-10 gap-x-6">
                    <div className="col-span-12">
                        <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
                            <div className="text-base font-medium group-[.mode--light]:text-white">
                                Outlets
                                {total > 0 && (
                                    <span className="ml-2 text-sm font-normal text-white">
                                        ({total} {total === 1 ? 'outlet' : 'outlets'})
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 md:ml-auto">
                                <Button
                                    variant="primary"
                                    className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    onClick={() => {
                                        const searchParams = new URLSearchParams(window.location.search);
                                        const companyId = searchParams.get("companyId");
                                        const projectId = searchParams.get("projectId");
                                        
                                        let addUrl = "/outlets/add";
                                        if (projectId) {
                                            addUrl += `?projectId=${projectId}`;
                                        } else if (companyId) {
                                            addUrl += `?companyId=${companyId}`;
                                        }
                                        
                                        navigate(addUrl);
                                    }}
                                >
                                    <Lucide icon="PenLine" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                                    Add New Outlet
                                </Button>

                                {/* New buttons for Outlet Group, Outlet Type, Outlet Category */}
                                <Button
                                    variant="outline-secondary"
                                    className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    onClick={() => {
                                        const searchParams = new URLSearchParams(window.location.search);
                                        const companyId = searchParams.get("companyId");
                                        const projectId = searchParams.get("projectId");
                                        
                                        let groupUrl = "/outlet-groups";
                                        if (projectId) {
                                            groupUrl += `?projectId=${projectId}`;
                                        } else if (companyId) {
                                            groupUrl += `?companyId=${companyId}`;
                                        }
                                        
                                        navigate(groupUrl);
                                    }}
                                >
                                    <Lucide icon="Package" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                                    Outlet Group
                                </Button>

                                <Button
                                    variant="outline-secondary"
                                    className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    onClick={() => {
                                        const searchParams = new URLSearchParams(window.location.search);
                                        const companyId = searchParams.get("companyId");
                                        const projectId = searchParams.get("projectId");
                                        
                                        let typeUrl = "/outlet-types";
                                        if (projectId) {
                                            typeUrl += `?projectId=${projectId}`;
                                        } else if (companyId) {
                                            typeUrl += `?companyId=${companyId}`;
                                        }
                                        
                                        navigate(typeUrl);
                                    }}
                                >
                                    <Lucide icon="Tag" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                                    Outlet Type
                                </Button>

                                <Button
                                    variant="outline-secondary"
                                    className="group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box"
                                    onClick={() => {
                                        const searchParams = new URLSearchParams(window.location.search);
                                        const companyId = searchParams.get("companyId");
                                        const projectId = searchParams.get("projectId");
                                        
                                        let categoryUrl = "/outlet-categories";
                                        if (projectId) {
                                            categoryUrl += `?projectId=${projectId}`;
                                        } else if (companyId) {
                                            categoryUrl += `?companyId=${companyId}`;
                                        }
                                        
                                        navigate(categoryUrl);
                                    }}
                                >
                                    <Lucide icon="Box" className="stroke-[1.3] w-4 h-4 mr-2" />{" "}
                                    Outlet Category
                                </Button>
                            </div>
                        </div>
                        <div className="mt-3.5">
                            <div className="flex flex-col box box--stacked">
                                <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
                                    <div>
                                        <form onSubmit={handleSearch}>
                                            <div className="relative">
                                                <Lucide
                                                    icon="Search"
                                                    className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                                                />
                                                <FormInput
                                                    type="text"
                                                    placeholder="Search outlets..."
                                                    className="pl-9 sm:w-64 rounded-[0.5rem]"
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                            </div>
                                        </form>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-x-3 gap-y-2 sm:ml-auto">
                                        <Button
                                            variant="outline-secondary"
                                            className="w-full sm:w-auto"
                                            onClick={() => {
                                                const searchParams = new URLSearchParams(window.location.search);
                                                const companyId = searchParams.get("companyId");
                                                const projectId = searchParams.get("projectId");
                                                
                                                let importUrl = "/outlets/import";
                                                const params = new URLSearchParams();
                                                if (projectId) params.append("projectId", projectId);
                                                if (companyId) params.append("companyId", companyId);
                                                if (params.toString()) importUrl += `?${params.toString()}`;
                                                
                                                navigate(importUrl);
                                            }}
                                        >
                                            <Lucide
                                                icon="Upload"
                                                className="stroke-[1.3] w-4 h-4 mr-2"
                                            />
                                            Import from Excel
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            className="w-full sm:w-auto"
                                            onClick={handleExport}
                                        >
                                            <Lucide
                                                icon="Download"
                                                className="stroke-[1.3] w-4 h-4 mr-2"
                                            />
                                            Export to Excel
                                        </Button>
                                        <Popover className="inline-block">
                                            {({ close }) => (
                                                <>
                                                    <Popover.Button
                                                        as={Button}
                                                        variant="outline-secondary"
                                                        className="w-full sm:w-auto"
                                                    >
                                                        <Lucide
                                                            icon="ArrowDownWideNarrow"
                                                            className="stroke-[1.3] w-4 h-4 mr-2"
                                                        />
                                                        Filter
                                                        <div className="flex items-center justify-center h-5 px-1.5 ml-2 text-xs font-medium border rounded-full bg-slate-100 dark:bg-darkmode-400">
                                                            3
                                                        </div>
                                                    </Popover.Button>
                                                    <Popover.Panel placement="bottom-end">
                                                        <div className="p-2">
                                                            <div>
                                                                <div className="text-left text-slate-500">
                                                                    Status
                                                                </div>
                                                                <FormSelect className="flex-1 mt-2">
                                                                    <option value="Active">Active</option>
                                                                    <option value="Inactive">Inactive</option>
                                                                </FormSelect>
                                                            </div>
                                                            <div className="mt-3">
                                                                <div className="text-left text-slate-500">
                                                                    Type
                                                                </div>
                                                                <FormSelect className="flex-1 mt-2">
                                                                    <option value="Regular">Regular</option>
                                                                    <option value="Competitor">Competitor</option>
                                                                </FormSelect>
                                                            </div>
                                                            <div className="flex items-center mt-4">
                                                                <Button
                                                                    variant="secondary"
                                                                    onClick={() => {
                                                                        close();
                                                                    }}
                                                                    className="w-32 ml-auto"
                                                                >
                                                                    Close
                                                                </Button>
                                                                <Button variant="primary" className="w-32 ml-2">
                                                                    Apply
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Popover.Panel>
                                                </>
                                            )}
                                        </Popover>
                                    </div>
                                </div>
                                <div className="relative overflow-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
                                    <Table className="border-b border-slate-200/60 text-xs">
                                        <Table.Thead className="text-xs sticky top-0 z-[55] bg-white dark:bg-darkmode-600 shadow-sm border-b border-slate-200/60">
                                            <Table.Tr>
                                                <Table.Td 
                                                    className="py-1.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none bg-white dark:bg-darkmode-600"
                                                    onClick={() => handleSort('name')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Name
                                                        <Lucide 
                                                            icon={sortField === 'name' 
                                                                ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown')
                                                                : 'ArrowUpDown'
                                                            } 
                                                            className={`w-3 h-3 ${sortField === 'name' ? '' : 'text-slate-400'}`}
                                                        />
                                                    </div>
                                                </Table.Td>
                                                <Table.Td 
                                                    className="py-1.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none bg-white dark:bg-darkmode-600"
                                                    onClick={() => handleSort('outletGroup')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Group
                                                        <Lucide 
                                                            icon={sortField === 'outletGroup' 
                                                                ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown')
                                                                : 'ArrowUpDown'
                                                            } 
                                                            className={`w-3 h-3 ${sortField === 'outletGroup' ? '' : 'text-slate-400'}`}
                                                        />
                                                    </div>
                                                </Table.Td>
                                                <Table.Td 
                                                    className="py-1.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none bg-white dark:bg-darkmode-600"
                                                    onClick={() => handleSort('outletCategory')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Category
                                                        <Lucide 
                                                            icon={sortField === 'outletCategory' 
                                                                ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown')
                                                                : 'ArrowUpDown'
                                                            } 
                                                            className={`w-3 h-3 ${sortField === 'outletCategory' ? '' : 'text-slate-400'}`}
                                                        />
                                                    </div>
                                                </Table.Td>
                                                <Table.Td 
                                                    className="py-1.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-700 select-none bg-white dark:bg-darkmode-600"
                                                    onClick={() => handleSort('outletType')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Type
                                                        <Lucide 
                                                            icon={sortField === 'outletType' 
                                                                ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown')
                                                                : 'ArrowUpDown'
                                                            } 
                                                            className={`w-3 h-3 ${sortField === 'outletType' ? '' : 'text-slate-400'}`}
                                                        />
                                                    </div>
                                                </Table.Td>
                                                <Table.Td className="py-1.5 bg-white dark:bg-darkmode-600">Address</Table.Td>
                                                <Table.Td className="py-1.5 bg-white dark:bg-darkmode-600">Outlet PIC</Table.Td>
                                                <Table.Td className="py-1.5 bg-white dark:bg-darkmode-600">Status</Table.Td>
                                                <Table.Td className="text-right py-1.5 bg-white dark:bg-darkmode-600">Action</Table.Td>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {outlets.map((outlet) => (
                                                <Table.Tr key={outlet.id} className="[&_td]:last:border-b-0">
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {outlet.name}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {outlet.outletGroup}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {outlet.outletCategory}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {outlet.outletType}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {outlet.address}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {outlet.contactPIC}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 border-dashed dark:bg-darkmode-600 text-xs">
                                                        {outlet.status ? "Active" : "Inactive"}
                                                    </Table.Td>
                                                    <Table.Td className="py-1.5 text-right border-dashed dark:bg-darkmode-600 text-xs">
                                                        <div className="flex justify-end items-center gap-3">
                                                            {/* Edit */}
                                                            <Link
                                                                to={(() => {
                                                                    const searchParams = new URLSearchParams(window.location.search);
                                                                    const companyId = searchParams.get("companyId");
                                                                    const projectId = searchParams.get("projectId");
                                                                    
                                                                    let editUrl = `/outlets/${outlet.id}/edit`;
                                                                    if (projectId) {
                                                                        editUrl += `?projectId=${projectId}`;
                                                                    } else if (companyId) {
                                                                        editUrl += `?companyId=${companyId}`;
                                                                    }
                                                                    
                                                                    return editUrl;
                                                                })()}
                                                                className="btn btn-outline-secondary p-2 rounded-lg"
                                                                title="Edit"
                                                            >
                                                                <Lucide icon="Pencil" className="w-3 h-3" />
                                                            </Link>

                                                            {/* Delete (unchanged) */}
                                                            <button
                                                                onClick={() => handleDelete(outlet.id)}
                                                                className="btn btn-outline-danger p-2 rounded-lg"
                                                                title="Delete"
                                                            >
                                                                <Lucide icon="Trash2" className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>

                                    </Table>
                                </div>
                                <div className="flex flex-col-reverse flex-wrap items-center p-5 flex-reverse gap-y-2 sm:flex-row">
                                    <div className="text-sm text-slate-600 dark:text-slate-400 mr-4">
                                        {total > 0 ? (
                                            <>
                                                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, total)} of {total} outlets
                                            </>
                                        ) : (
                                            'No outlets found'
                                        )}
                                    </div>
                                    <Pagination className="flex-1 w-full mr-auto sm:w-auto">
                                        <Pagination.Link 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage > 1) handleFirstPage();
                                            }}
                                            className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronsLeft" className="w-3 h-3" />
                                        </Pagination.Link>
                                        <Pagination.Link 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage > 1) handlePrevPage();
                                            }}
                                            className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronLeft" className="w-3 h-3" />
                                        </Pagination.Link>
                                        {totalPages > 0 && (
                                            <>
                                                {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                                                    let pageNum: number;
                                                    if (totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        pageNum = totalPages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }
                                                    return (
                                                        <Pagination.Link
                                                            key={pageNum}
                                                            active={currentPage === pageNum}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                setCurrentPage(pageNum);
                                                            }}
                                                            className="cursor-pointer"
                                                        >
                                                            {pageNum}
                                                        </Pagination.Link>
                                                    );
                                                })}
                                            </>
                                        )}
                                        <Pagination.Link 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage < totalPages) handleNextPage();
                                            }}
                                            className={currentPage >= totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronRight" className="w-3 h-3" />
                                        </Pagination.Link>
                                        <Pagination.Link 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage < totalPages) handleLastPage();
                                            }}
                                            className={currentPage >= totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                        >
                                            <Lucide icon="ChevronsRight" className="w-3 h-3" />
                                        </Pagination.Link>
                                    </Pagination>
                                    <FormSelect 
                                        className="sm:w-20 rounded-[0.5rem]"
                                        value={pageSize}
                                        onChange={handlePageSizeChange}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={250}>250</option>
                                        <option value={500}>500</option>
                                    </FormSelect>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
    );
}

export default OutletsPage;